
import { Target, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface StrategicRoadmapProps {
  maturityLevel: string;
  dimensionScores: Record<string, number>;
  lowestDimension: string;
}

export const StrategicRoadmap = ({ 
  maturityLevel, 
  dimensionScores, 
  lowestDimension 
}: StrategicRoadmapProps) => {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Target className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold text-primary">Strategic Roadmap</h2>
      </div>
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold mb-4">Short-term Actions (3-6 months)</h3>
          <ul className="space-y-4">
            {getShortTermActions(maturityLevel, dimensionScores, lowestDimension).map((action, index) => (
              <li key={index} className="flex items-start gap-3">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span className="text-muted-foreground">{action}</span>
              </li>
            ))}
          </ul>
        </div>
        <Separator />
        <div>
          <h3 className="text-xl font-semibold mb-4">Mid-term Strategy (6-12 months)</h3>
          <ul className="space-y-4">
            {getMidTermActions(maturityLevel, dimensionScores, lowestDimension).map((action, index) => (
              <li key={index} className="flex items-start gap-3">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span className="text-muted-foreground">{action}</span>
              </li>
            ))}
          </ul>
        </div>
        <Separator />
        <div>
          <h3 className="text-xl font-semibold mb-4">Long-term Vision (12+ months)</h3>
          <ul className="space-y-4">
            {getLongTermActions(maturityLevel, dimensionScores, lowestDimension).map((action, index) => (
              <li key={index} className="flex items-start gap-3">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span className="text-muted-foreground">{action}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
};

// Helper functions from the original file
function getShortTermActions(maturityLevel: string, scores: Record<string, number>, lowestDimension: string): string[] {
  return [
    `Establish baseline metrics for ${lowestDimension} improvement (Based on current score of ${scores[lowestDimension]}/5)`,
    `Create an AI governance committee to oversee ${lowestDimension} initiatives (Critical for advancing from ${maturityLevel})`,
    "Develop comprehensive data quality assessment framework (Foundation for AI maturity advancement)",
    `Initiate targeted AI awareness training focused on ${lowestDimension} (Address immediate skill gaps)`,
    "Implement quick-win AI use cases aligned with business priorities (Build momentum and demonstrate value)"
  ];
}

function getMidTermActions(maturityLevel: string, scores: Record<string, number>, lowestDimension: string): string[] {
  return [
    `Develop and implement ${lowestDimension} improvement roadmap (Address key gap identified in assessment)`,
    "Establish AI Center of Excellence with clear governance structure (Scale AI capabilities systematically)",
    `Create comprehensive talent development program focusing on ${lowestDimension} (Build long-term capability)`,
    "Implement data governance framework aligned with AI strategy (Enable sustainable AI growth)",
    "Launch strategic AI pilot projects in high-impact areas (Demonstrate business value at scale)"
  ];
}

function getLongTermActions(maturityLevel: string, scores: Record<string, number>, lowestDimension: string): string[] {
  return [
    "Scale successful AI initiatives enterprise-wide (Build on proven successes)",
    `Establish innovation lab focused on ${lowestDimension} advancement (Drive continuous improvement)`,
    "Develop strategic AI ecosystem partnerships (Access cutting-edge capabilities)",
    "Create AI-first culture through organizational transformation (Enable sustainable adoption)",
    "Build proprietary AI solutions aligned with business strategy (Create competitive advantage)"
  ];
}
